// Documents API
import api, { type AxiosResponse } from './client';
import type {
  DocumentCategory,
  Document,
  DocumentDownload,
  DocumentStats,
  CategoryWithDocuments,
  QueryParams,
  PendingCompanyConfirmation,
  CompanyConfirmationReport,
} from './types';

export const documentsAPI = {
  // Categories
  getCategories: (params?: QueryParams): Promise<AxiosResponse<DocumentCategory[]>> =>
    api.get('/documents/categories/', { params }),

  // Fetch EVERY folder across all pages. The list endpoint paginates
  // (default page_size 20, server max 100), so any caller that builds a
  // folder picker MUST walk all pages or it silently drops folders beyond
  // the first page — a tenant can have hundreds of folders (one per part).
  // The page cap guards against a non-terminating response.
  getAllCategories: async (params?: QueryParams): Promise<DocumentCategory[]> => {
    const all: DocumentCategory[] = [];
    const MAX_PAGES = 50;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const res = await api.get('/documents/categories/', {
        params: { ...params, page, page_size: 100 },
      });
      const data = res.data as unknown;
      if (Array.isArray(data)) {
        all.push(...(data as DocumentCategory[]));
        break;
      }
      const paged = data as { results?: DocumentCategory[]; next?: string | null };
      const pageItems = paged.results || [];
      all.push(...pageItems);
      if (!paged.next || pageItems.length === 0) break;
    }
    return all;
  },

  /** Every document matching `params`, walking all pages.
   *  A bare getDocuments() returns only the first page (DRF PAGE_SIZE=20,
   *  max_page_size=100), so any caller that renders a complete list — or
   *  derives something from it, like the supplier tab's folder picker — must
   *  page through rather than trusting one response. Mirrors getAllCategories. */
  getAllDocuments: async (params?: QueryParams): Promise<Document[]> => {
    const all: Document[] = [];
    const MAX_PAGES = 50;
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const res = await api.get('/documents/documents/', {
        params: { ...params, page, page_size: 100 },
      });
      const data = res.data as unknown;
      if (Array.isArray(data)) {
        all.push(...(data as Document[]));
        break;
      }
      const paged = data as { results?: Document[]; next?: string | null };
      const pageItems = paged.results || [];
      all.push(...pageItems);
      if (!paged.next || pageItems.length === 0) break;
    }
    return all;
  },

  getCategory: (id: number): Promise<AxiosResponse<DocumentCategory>> =>
    api.get(`/documents/categories/${id}/`),

  createCategory: (data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.post('/documents/categories/', data),

  updateCategory: (id: number, data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.patch(`/documents/categories/${id}/`, data),

  deleteCategory: (
    id: number,
    options?: { cascade?: boolean; reassignTo?: number | null },
  ): Promise<AxiosResponse<void>> => {
    // CR-5379: two-mode folder delete. Send cascade / reassign_to as
    // query params so the backend's destroy override can read them
    // without us having to hand-roll a custom request body for an
    // HTTP DELETE.
    const params: Record<string, string> = {};
    if (options?.cascade) params['cascade'] = 'true';
    if (!options?.cascade && options?.reassignTo != null) {
      params['reassign_to'] = String(options.reassignTo);
    }
    return api.delete(`/documents/categories/${id}/`, { params });
  },

  getCategoryDocuments: (id: number): Promise<AxiosResponse<Document[]>> =>
    api.get(`/documents/categories/${id}/documents/`),

  // CR-7754: a folder's Details window — location, contents and access,
  // counted over what the caller can see. Customer side only (403 for
  // suppliers).
  getCategoryDetails: (id: number): Promise<AxiosResponse<FolderDetails>> =>
    api.get(`/documents/categories/${id}/details/`),

  // CR-7754: a folder's History tab, in the same Changelog shape as a
  // document's Audit History (newest first).
  getCategoryHistory: (id: number): Promise<AxiosResponse<DocumentHistoryEntry[]>> =>
    api.get(`/documents/categories/${id}/history/`),

  // Documents
  getDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Document[] }>> =>
    api.get('/documents/documents/', { params }),

  getDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.get(`/documents/documents/${id}/`),

  createDocument: (data: FormData): Promise<AxiosResponse<Document>> =>
    api.post('/documents/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateDocument: (id: number, data: FormData | Partial<Document>): Promise<AxiosResponse<Document>> =>
    api.patch(`/documents/documents/${id}/`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
    }),

  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/documents/documents/${id}/`),

  downloadDocument: (id: number): Promise<AxiosResponse<{ file_url: string; title: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/download/`),

  // CR-5331: getPreviewUrl removed. The previous implementation embedded
  // the permanent JWT in the URL (CWE-200) AND was rejected by the
  // backend's preview endpoint (which only honours u/exp/sig signed
  // params). Use downloadDocument(id) to mint a short-lived signed link
  // and open response.data.file_url instead.

  getDocumentsByCategory: (params?: QueryParams): Promise<AxiosResponse<CategoryWithDocuments[]>> =>
    api.get('/documents/documents/by_category/', { params }),

  getDocumentStats: (params?: QueryParams): Promise<AxiosResponse<DocumentStats>> =>
    api.get('/documents/documents/stats/', { params }),

  // CR-5330: Audit history feed for the document details popup. The
  // backend's history action combines the upload event, downloads and
  // read-receipts into a single Changelog-shaped feed (newest first).
  getDocumentHistory: (id: number): Promise<AxiosResponse<DocumentHistoryEntry[]>> =>
    api.get(`/documents/documents/${id}/history/`),

  // CR-5328: Version-history endpoints. The current file lives on
  // Document; older snapshots live in DocumentVersion. uploadNewVersion
  // snapshots the previous file then replaces with the new one.
  getDocumentVersions: (id: number): Promise<AxiosResponse<DocumentVersionEntry[]>> =>
    api.get(`/documents/documents/${id}/versions/`),

  uploadNewVersion: (id: number, data: FormData): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/upload_version/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  downloadVersion: (id: number, versionId: number): Promise<AxiosResponse<{ file_url: string; title: string; version_number: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/versions/${versionId}/download/`),

  // CR-5329: Record a Read & Accept confirmation. Idempotent —
  // re-clicking is a no-op. Returns the refreshed Document with
  // has_accepted=true / accepted_at populated for the caller.
  acceptDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/accept/`),

  // CR-5858: company-level "Read & Understood" confirmation. confirmCompany
  // records (or refreshes) a confirmation by the active supplier company.
  // getPendingCompanyConfirmations drives the global supplier blocking modal.
  // getCompanyConfirmationReport returns the customer-side per-company
  // confirmation status for a single document.
  confirmCompany: (id: number): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/company_confirm/`),

  getPendingCompanyConfirmations: (): Promise<AxiosResponse<PendingCompanyConfirmation[]>> =>
    api.get(`/documents/documents/pending_company_confirmations/`),

  getCompanyConfirmationReport: (id: number): Promise<AxiosResponse<CompanyConfirmationReport>> =>
    api.get(`/documents/documents/${id}/company_confirmation_report/`),

  // CR-8197: Communications tab. The owner of a document holds one private
  // conversation per company that can see it; everyone else only reaches
  // their own, so `counterpart` is only honoured for the owner (and Helix).
  getDocumentConversations: (id: number): Promise<AxiosResponse<DocumentConversations>> =>
    api.get(`/documents/documents/${id}/conversations/`),

  getDocumentMessages: (id: number, counterpart?: number): Promise<AxiosResponse<DocumentMessage[]>> =>
    api.get(`/documents/documents/${id}/messages/`, { params: counterpart ? { counterpart } : {} }),

  postDocumentMessage: (id: number, text: string, counterpart?: number): Promise<AxiosResponse<DocumentMessage>> =>
    api.post(`/documents/documents/${id}/messages/`, { text }, { params: counterpart ? { counterpart } : {} }),

  markDocumentMessagesRead: (id: number, counterpart?: number): Promise<AxiosResponse<void>> =>
    api.post(`/documents/documents/${id}/messages/read/`, null, { params: counterpart ? { counterpart } : {} }),

  // Downloads
  getDownloads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: DocumentDownload[] }>> =>
    api.get('/documents/downloads/', { params }),
};

// CR-5330: Shape returned by the documents history endpoint. Mirrors the
// ChangelogEntry interface in components/common/Changelog.tsx (with id
// loosened to string|number — this endpoint synthesises ids like
// 'upload-12' / 'download-44' from heterogeneous tables).
export interface DocumentHistoryEntry {
  id: string | number;
  change_date: string;
  changed_by: string;
  changed_by_company?: string | null;
  field_changed: string;
  old_value: string | null;
  new_value: string;
  event_type?: string;
}

/** CR-7754: payload of GET /documents/categories/:id/details/. */
export interface FolderDetails {
  id: number;
  name: string;
  description: string;
  /** Folder names from the main folder down to this one. */
  path: string[];
  created_at: string;
  /** Who created it — null for folders made before creation was recorded. */
  created_by: string | null;
  updated_at: string;
  direct_file_count: number;
  subfolder_count: number;
  /** Files in this folder and in every sub-folder under it. */
  total_file_count: number;
  total_size_bytes: number;
  /** Human-readable total, e.g. "4.2 MB". */
  total_size_display: string;
  last_upload: { title: string; at: string; by: string } | null;
  access: {
    level: 'private' | 'shared' | 'public';
    /** Suppliers it is shared with that the caller may see named. */
    shared_with: string[];
    /** Shares to companies the caller may not see named. */
    hidden_count: number;
  };
}

// CR-8197: one entry on a document's Communications tab.
export interface DocumentMessage {
  id: number;
  /** 'version_note' entries are "Version n uploaded: <notes>" lines. */
  kind: 'message' | 'version_note';
  text: string;
  sender_name: string;
  sender_company: number;
  sender_company_name: string;
  /** The company this conversation is with (the owner's counterpart). */
  counterpart_company: number;
  /** True when the signed-in user sent it. */
  is_own: boolean;
  created_at: string;
}

// CR-8197: a conversation the viewer can open, for the picker and tab badge.
export interface DocumentConversation {
  company_id: number;
  company_name: string;
  unread_count: number;
  message_count: number;
  last_message_at: string | null;
}

// CR-8197: the viewer's side decides what the tab shows — the owner (and Helix,
// read-only) get a company picker; a counterpart only ever sees its own thread.
export interface DocumentConversations {
  side: 'owner' | 'counterpart' | 'observer';
  can_post: boolean;
  conversations: DocumentConversation[];
  unread_total: number;
}

// CR-5328: Shape returned by the version-history endpoint. Mirrors the
// DocumentVersionSerializer fields.
export interface DocumentVersionEntry {
  id: number;
  document: number;
  version_number: string;
  notes: string;
  uploaded_by: number | null;
  uploaded_by_name: string;
  uploaded_at: string;
  formatted_file_size: string;
  file_extension: string;
  mime_type: string;
}
